import type { ReactNode } from 'react';
import { Fragment } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { schoolYearEndYear } from '../../../core/schoolYear';
import { APP_LINK_STATE } from '../../Home/navRoutes';
import type { Crumb } from '../../IndividualPlan/TopBar';
import { COLORS } from '../../IndividualPlan/tokens';
import { formatGeneratedDate } from '../components/ProgramEvaluationHeader';
import { PE_ROUTE, studentsHref } from '../listKeys';
import { useProgramEvaluationReport } from '../program-evaluation-hook';
import { ProgramEvaluationShell } from '../ProgramEvaluationShell';
import { reportSettings, type PeDrilldown, type PeReport, type PeReportSettings, type ScoreUnit } from '../types';

export type LoadedReport = {
  report: PeReport;
  drilldown: PeDrilldown;
  generatedOn: string;
  version: number | null;
  settings: PeReportSettings;
  resetYear: boolean;
  schoolYear: string;
  endYear: number | null;
  unit: ScoreUnit;
};

export const sameDataSentence = (generatedOn: string): string =>
  `The same data the report was generated from on ${generatedOn}. Every number here adds up to the report.`;

export function DrilldownPage({
  crumbs,
  title,
  subtitle,
  opened,
  children,
}: {
  crumbs: Crumb[];
  title: string;
  subtitle?: string;
  opened?: string;
  children: ReactNode;
}) {
  return (
    <ProgramEvaluationShell crumbs={crumbs}>
      <Box component="header" sx={{ mb: 3 }}>
        <Typography component="h1" sx={{ fontSize: 32, fontWeight: 700, color: COLORS.ink, lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, mt: 0.75, maxWidth: 760 }}>
            {subtitle}
          </Typography>
        )}
        {opened && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, mt: 1 }}>
            {`Opened from: ${opened}`}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
    </ProgramEvaluationShell>
  );
}

export function DrilldownTip({ children }: { children: string }) {
  return (
    <Box
      data-testid="drilldown-tip"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 2,
        py: 1.5,
        borderRadius: '8px',
        bgcolor: COLORS.panel,
        border: `1px solid ${COLORS.divider}`,
        fontSize: 14,
        fontWeight: 500,
        color: COLORS.ink,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color: COLORS.link, mt: '2px', flexShrink: 0 }} />
      <span>{children}</span>
    </Box>
  );
}

export function NotFoundPage({
  crumbs,
  loaded,
  message,
}: {
  crumbs: Crumb[];
  loaded: LoadedReport;
  message: string;
}) {
  return (
    <DrilldownPage crumbs={crumbs} title="Program Evaluation" subtitle={sameDataSentence(loaded.generatedOn)}>
      <DrilldownTip>{message}</DrilldownTip>
    </DrilldownPage>
  );
}

export function DetailCard({
  title,
  description,
  foot,
  narrative,
  children,
}: {
  title?: string;
  description?: string;
  foot?: string;
  narrative?: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: '16px 20px 20px',
        bgcolor: '#FFFFFF',
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
      }}
    >
      {(title || description) && (
        <Box>
          {title && (
            <Typography component="h2" sx={{ m: 0, fontSize: 18, lineHeight: '24px', fontWeight: 700, color: COLORS.ink }}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 500, color: COLORS.muted }}>{description}</Typography>
          )}
        </Box>
      )}
      {children}
      {foot && <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>{foot}</Typography>}
      {narrative && (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>{narrative}</Typography>
      )}
    </Box>
  );
}

export type StripItem = { label: string; value: ReactNode };

export function DetailStrip({ items }: { items: StripItem[] }) {
  return (
    <Box
      data-testid="detail-strip"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 3,
        px: 2.5,
        py: 2,
        flexWrap: 'wrap',
        bgcolor: '#FFFFFF',
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
      }}
    >
      {items.map((item, i) => (
        <Fragment key={item.label}>
          {i > 0 && <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: COLORS.hairline }} />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 700, letterSpacing: 0.5, color: COLORS.muted }}
            >
              {item.label}
            </Typography>
            <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>
              {item.value}
            </Typography>
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

export function StudentsLinkCard({
  count,
  listKey,
  noun = 'here',
  population = 'active',
}: {
  count: number;
  listKey: string;
  noun?: string;
  population?: 'active' | 'served';
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2.5,
        py: 2,
        bgcolor: '#FFFFFF',
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
          {population === 'served'
            ? `The ${count.toLocaleString('en-US')} students served ${noun} this year`
            : `The ${count.toLocaleString('en-US')} students counted ${noun} now`}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>
          {population === 'served'
            ? 'As counted when the report was generated, not today’s roster, including any who reclassified during the year.'
            : 'As counted when the report was generated, not today’s roster. A student who reclassified this year is in the met count, not in this list.'}
        </Typography>
      </Box>
      <Link
        component={RouterLink}
        to={studentsHref(listKey)}
        state={APP_LINK_STATE}
        underline="hover"
        sx={{ display: 'inline-flex', alignItems: 'center', color: COLORS.link, fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        View students
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </Link>
    </Box>
  );
}

export function DrilldownFrame({
  crumbs,
  children,
}: {
  crumbs: Crumb[];
  children: (loaded: LoadedReport) => ReactNode;
}) {
  const { report, content, generatedAt, version, loading, error } = useProgramEvaluationReport();

  if (loading) {
    return (
      <ProgramEvaluationShell crumbs={crumbs}>
        <CircularProgress aria-label="Loading Program Evaluation report" />
      </ProgramEvaluationShell>
    );
  }
  if (error) {
    return (
      <ProgramEvaluationShell crumbs={crumbs}>
        <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          Program Evaluation is temporarily unavailable
        </Typography>
      </ProgramEvaluationShell>
    );
  }
  if (!report) {
    return (
      <ProgramEvaluationShell crumbs={crumbs}>
        <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          A Program Evaluation report has not been generated yet for this district.
        </Typography>
      </ProgramEvaluationShell>
    );
  }

  const generatedOn = formatGeneratedDate(report.metadata.generated_at || generatedAt || '');
  if (report.drilldown === undefined) {
    return (
      <ProgramEvaluationShell crumbs={crumbs}>
        <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          The students behind these counts are temporarily unavailable. Try again in a moment.
        </Typography>
      </ProgramEvaluationShell>
    );
  }
  if (!report.drilldown) {
    return (
      <DrilldownPage crumbs={crumbs} title="Program Evaluation" subtitle={sameDataSentence(generatedOn)}>
        <DrilldownTip>
          The students behind these counts aren&apos;t available for this generation. Regenerate the report to open them.
        </DrilldownTip>
        <Link component={RouterLink} to={PE_ROUTE} state={APP_LINK_STATE} underline="hover" sx={{ color: COLORS.link, fontWeight: 600 }}>
          Back to the report
        </Link>
      </DrilldownPage>
    );
  }

  const schoolYear = report.district.school_year;
  return (
    <>
      {children({
        report,
        drilldown: report.drilldown,
        generatedOn,
        version,
        settings: reportSettings(report),
        resetYear: report.growth.composite.first_post_reset_year === true,
        schoolYear,
        endYear: schoolYearEndYear(schoolYear),
        unit: content?.unit_mode ?? 'proficiency_level',
      })}
    </>
  );
}
